import { computed } from 'vue'

import { useAuthStore } from '@ezio/features/auth/store'
import { useAgenciesStore } from '@ezio/features/agencies/store'

import { AgencyPermissions } from './types'

export * from './types'
export * from './positions'
export * from './blob'
export * from './assign'
export { usePermissionsStore } from './store'

/**
 * Lecture des permissions effectives de la session (portées par
 * useAuthStore().user.permissions, matrice dense indexée par id d'agence).
 *
 * Défaut PERMISSIF : toute clé absente = autorisé. Un manager sans permissions
 * (null/absent) n'est pas restreint ⇒ tout est autorisé. Ne jette jamais.
 */
export const usePermissions = () => {
    const permissionsFor = (agencyId?: string | number): AgencyPermissions | null => {
        const user = useAuthStore().user
        if (!user?.permissions) {
            return null
        }
        // repli sur l'agence de la session : agenciesStore.current est posé de façon
        // asynchrone et peut manquer au moment du guard de navigation initial
        const id = agencyId ?? useAgenciesStore().current?.id ?? user.agencyId
        if (id == null) {
            return null
        }
        return user.permissions[String(id)] ?? null
    }

    const canRead = (domain: string, agencyId?: string | number): boolean =>
        permissionsFor(agencyId)?.[domain]?.read !== false

    const canWrite = (domain: string, agencyId?: string | number): boolean =>
        permissionsFor(agencyId)?.[domain]?.write !== false

    const can = (domain: string, action: string, agencyId?: string | number): boolean =>
        permissionsFor(agencyId)?.[domain]?.actions?.[action] !== false

    // vrai si l'action est permise sur au moins un établissement : pour les
    // pages de portée organisation, qui n'ont pas d'agence de référence
    const canSomewhere = (domain: string, action: string): boolean => {
        const permissions = useAuthStore().user?.permissions
        if (!permissions) {
            return true
        }
        const entries = Object.values(permissions)
        return !entries.length || entries.some((p) => p?.[domain]?.actions?.[action] !== false)
    }

    const isRestricted = computed(() => !!useAuthStore().user?.permissions)

    return {
        permissionsFor,
        canRead,
        canWrite,
        can,
        canSomewhere,
        isRestricted
    }
}
